import { ShapeStream, Shape } from "@electric-sql/client";
import { getBackendBaseUrl } from "@/managers/backend-api-client/initializeBackendApiManager";
import { ElectricSession, ElectricSessionEvent } from "./types";

export interface SessionElectricManagerOptions {
  onSessionDataUpdate: (sessionData: ElectricSession) => void;
  onSessionEventsUpdate: (events: ElectricSessionEvent[]) => void;
  onError: (error: Error) => void;
  getAuthToken: () => string | null;
}

interface SessionSubscription {
  sessionStream: ShapeStream<ElectricSession>;
  sessionShape: Shape<ElectricSession>;
  eventsStream: ShapeStream<ElectricSessionEvent>;
  eventsShape: Shape<ElectricSessionEvent>;
  unsubscribeSession: () => void;
  unsubscribeEvents: () => void;
}

export class SessionElectricManager {
  private options: SessionElectricManagerOptions;
  private activeSubscription: SessionSubscription | null = null;
  private currentSessionId: string | null = null;

  constructor(options: SessionElectricManagerOptions) {
    this.options = options;
  }

  async subscribeToSession(sessionId: string): Promise<void> {
    const authToken = this.options.getAuthToken();
    if (!authToken) {
      throw new Error("No authentication token available");
    }

    // Clean up existing subscription if it exists
    if (this.activeSubscription) {
      this.unsubscribeFromSession();
    }

    this.currentSessionId = sessionId;

    console.log(`SessionElectricManager: Subscribing to session: ${sessionId}`);

    try {
      // Create session data stream
      const sessionStream = new ShapeStream<ElectricSession>({
        url: `${getBackendBaseUrl()}/internal/v1/electric/v1/shape`,
        params: {
          table: "sessions",
          where: `id = '${sessionId}'`,
        },
        headers: {
          Authorization: `OAuthAccessToken ${authToken}`,
        },
        parser: {
          // Normalize timestamps
          timestamptz: (date: string) => new Date(date).toISOString(),
        },
        backoffOptions: {
          initialDelay: 1000,
          maxDelay: 60000,
          multiplier: 3,
        },
      });

      // Create session events stream
      const eventsStream = new ShapeStream<ElectricSessionEvent>({
        url: `${getBackendBaseUrl()}/internal/v1/electric/v1/shape`,
        params: {
          table: "session_events",
          where: `session_id = '${sessionId}'`,
        },
        headers: {
          Authorization: `OAuthAccessToken ${authToken}`,
        },
        parser: {
          // Normalize timestamps
          timestamptz: (date: string) => new Date(date).toISOString(),
        },
        backoffOptions: {
          initialDelay: 1000,
          maxDelay: 60000,
          multiplier: 3,
        },
      });

      // Create shapes and subscribe
      const sessionShape = new Shape<ElectricSession>(sessionStream);
      const eventsShape = new Shape<ElectricSessionEvent>(eventsStream);

      // Wait for initial data to load
      console.log(`SessionElectricManager: Loading initial data for session: ${sessionId}`);

      // Wait for both shapes to have their initial data
      const [sessionRows, eventsRows] = await Promise.all([sessionShape.rows, eventsShape.rows]);

      console.log(`SessionElectricManager: Initial data loaded for session: ${sessionId}`, {
        sessionCount: sessionRows.length,
        eventsCount: eventsRows.length,
      });

      // Send initial data
      if (sessionRows.length > 0) {
        this.options.onSessionDataUpdate(sessionRows[0]);
      }

      this.options.onSessionEventsUpdate(eventsRows);

      // Set up subscriptions for ongoing updates
      const unsubscribeSession = sessionShape.subscribe(({ rows }: { rows: ElectricSession[] }) => {
        console.log(`SessionElectricManager: Received session update for session: ${sessionId}`, {
          rows,
        });

        if (rows.length > 0) {
          this.options.onSessionDataUpdate(rows[0]);
        }
      });

      const unsubscribeEvents = eventsShape.subscribe(
        ({ rows }: { rows: ElectricSessionEvent[] }) => {
          console.log(`SessionElectricManager: Received events update for session: ${sessionId}`, {
            rowCount: rows.length,
          });

          this.options.onSessionEventsUpdate(rows);
        }
      );

      // Store the subscription for cleanup
      this.activeSubscription = {
        sessionStream,
        sessionShape,
        eventsStream,
        eventsShape,
        unsubscribeSession,
        unsubscribeEvents,
      };

      console.log(`SessionElectricManager: Successfully subscribed to session: ${sessionId}`);
    } catch (error) {
      console.error(`SessionElectricManager: Failed to subscribe to session: ${sessionId}`, error);
      this.options.onError(error as Error);
      throw error;
    }
  }

  unsubscribeFromSession(): void {
    if (!this.activeSubscription) {
      console.log("SessionElectricManager: No active subscription to clean up");
      return;
    }

    const sessionId = this.currentSessionId;
    console.log(`SessionElectricManager: Unsubscribing from session: ${sessionId}`);

    try {
      this.activeSubscription.unsubscribeSession();
      this.activeSubscription.unsubscribeEvents();

      this.activeSubscription = null;
      this.currentSessionId = null;

      console.log(`SessionElectricManager: Successfully unsubscribed from session: ${sessionId}`);
    } catch (error) {
      console.error(`SessionElectricManager: Error during unsubscribe: ${sessionId}`, error);
      this.options.onError(error as Error);
    }
  }

  isSubscribed(): boolean {
    return this.activeSubscription !== null;
  }

  getCurrentSessionId(): string | null {
    return this.currentSessionId;
  }

  cleanup(): void {
    console.log("SessionElectricManager: Cleaning up");
    this.unsubscribeFromSession();
  }
}
